/**
 * A custom element that creates a horizontal-scroll-carousel
 * @element horizontal-scroll-carousel
 *
 * @attribute show-bullets - If present, bullet buttons will be added to the carousel
 * @attribute show-arrows - If present, arrow buttons will be added to the carousel
 * @attribute arrows:media - If present, arrows will only be shown when the media query is met
 * @attribute enable-mouse-drag - If present, mouse dragging will be enabled on the carousel
 * @attribute client:media - If present, the carousel will only be initialized when the media query is met
 *
 * @example
 *   <horizontal-scroll-carousel client:visible show-arrows>
 *     <ul class='scroll-list' data-scroll-list>
 *       {% for product in collection.products limit: section.settings.limit %}
 *         <li class='scroll-item' data-scroll-item='{{ forloop.index }}'>
 *           {% render 'product-card', product: product %}
 *         </li>
 *       {% endfor %}
 *     </ul>
 *   </horizontal-scroll-carousel>
 */
class HorizontalScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.initRespectingMediaQuery();
  }

  /**
   * Conditionally initializes the carousel based on the 'client:media' attribute.
   * Checks to see if the carousel should only be initialized when a certain media query is met.
   * If so, we add an event listener to the media query to initialize or destroy the carousel when the media query is met or not met, respectively.
   * Else, we initialize the carousel immediately.
   *
   * @returns {void}
   */
  initRespectingMediaQuery() {
    if (!this.hasAttribute('client:media')) {
      this.init();
      return;
    }

    this.mediaQuery = globalThis.matchMedia(this.getAttribute('client:media'));
    if (this.mediaQuery.matches) {
      this.init();
    }

    this.mediaQuery.addEventListener('change', () => {
      if (this.mediaQuery.matches) {
        this.init();
      } else {
        this.destroy();
      }
    });
  }

  /**
   * Initializes the carousel by adding the arrow buttons, bullet buttons, and setting up the IntersectionObserver
   *
   * @returns {void}
   */
  init() {
    this.assignVariables();

    if (this.showArrows) {
      this.initArrows();
    }

    if (this.showBullets) {
      this.addBullets();
    }

    this.enableMouseDrag();
    this.maintainSetOfInViewIndexes();
  }

  /**
   * Initializes the arrow buttons, and where an arrow:media attribute is present, adds or removes the arrow buttons based on the media query
   *
   * @returns {void}
   */
  initArrows() {
    if (!this.hasAttribute('arrows:media')) {
      // if there's no media query, add the arrow buttons immediately
      this.addArrowButtons();
    }

    const arrowsMediaQuery = globalThis.matchMedia(
      this.getAttribute('arrows:media')
    );

    // If the media query match changes, add or remove the arrow buttons
    arrowsMediaQuery.addEventListener('change', () => {
      if (arrowsMediaQuery.matches) {
        this.addArrowButtons();
      } else {
        this.removeArrowButtons();
      }
    });

    // If the media query matches on first load add the arrow buttons
    if (arrowsMediaQuery.matches) {
      this.addArrowButtons();
    }
  }

  /**
   * Removes the carousel's buttons and bullets. Event listers are automatically removed when the elements are removed.
   *
   * @returns {void}
   */
  destroy() {
    this.bullets && this.bullets.remove();
    this.removeArrowButtons();
    this.observer && this.observer.disconnect();
  }

  assignVariables() {
    this.showBullets = this.hasAttribute('show-bullets');
    this.showArrows = this.hasAttribute('show-arrows');
    this.list = this.querySelector('[data-scroll-list]');
    this.items = this.querySelectorAll('[data-scroll-item]');
    this.inViewIndexes = new Set();
  }

  /**
   * Adds arrow buttons to the carousel and assigns event listeners to them
   *
   * @returns {void}
   */
  async addArrowButtons() {
    // Append the arrow buttons to the carousel
    this.insertAdjacentHTML('beforeend', this.arrowButton('left'));
    this.insertAdjacentHTML('beforeend', this.arrowButton('right'));

    // Add event listeners to the arrow buttons
    this.leftButton = this.querySelector('[data-scroll-left]');
    this.rightButton = this.querySelector('[data-scroll-right]');
    this.leftButton.addEventListener('click', () => this.goTo('previous'));
    this.rightButton.addEventListener('click', () => this.goTo('next'));

    // This is async. We need the buttons added immediately, as we then set them to be enabled/disabled.
    // The arrow image can be added in as and when.
    const { default: arrowSvgString } = await import('./arrow-right.svg?raw');
    if (arrowSvgString) {
      this.rightButton.innerHTML = arrowSvgString;
      this.leftButton.innerHTML = arrowSvgString;
    }
  }

  /**
   * Removes the arrow buttons from the carousel
   *
   * @returns {void}
   */
  removeArrowButtons() {
    if (this.leftButton) {
      this.leftButton.remove();
    }

    if (this.rightButton) {
      this.rightButton.remove();
    }
  }

  /**
   * Returns the HTML for the arrow buttons
   *
   * @returns {String} The HTML for the arrow buttons
   */
  arrowButton(direction) {
    const ariaLabel =
      globalThis.PxlApp.translations.accessibility.scroll.replace(
        '{{ direction }}',
        direction
      );
    return `<button class="btn-unstyled scroll-button scroll-button-${direction}" data-scroll-${direction} aria-label="${ariaLabel}"></button>`;
  }

  /**
   * Adds bullet buttons to the carousel and assigns event listeners to them
   *
   * @returns {void}
   */
  addBullets() {
    // Append the bullet buttons to the carousel
    const bulletsHtml = `<div class="scroll-bullets" data-scroll-bullets>
    ${[...this.items].map((item, index) => {
      return `<button class="scroll-bullet" data-scroll-bullet="${index}"></button>`;
    })}
    </div>`;

    this.insertAdjacentHTML('beforeend', bulletsHtml);

    // Add event listeners to the bullet buttons
    this.bullets = this.querySelectorAll('[data-scroll-bullet]');
    for (const bullet of this.bullets) {
      bullet.addEventListener('click', event => {
        const bullet = event.target;

        const index = bullet.dataset.scrollBullet;
        this.scrollToIndex(index);
      });
    }
  }

  /**
   * Enables mouse dragging on the carousel
   * By default, with horizontal-scroll-carousel, you can't drag the carousel with the mouse (but you can with touch).
   * @reference https://codepen.io/jpkeisala-the-lessful/pen/poKRrPa
   *
   * @returns {void}
   */
  enableMouseDrag() {
    let isDown = false;
    let startX;
    let scrollLeft;

    if (!this.list) {
      return;
    }

    this.list.addEventListener('mousedown', event => {
      isDown = true;
      startX = event.pageX - this.list.offsetLeft;
      scrollLeft = this.list.scrollLeft;
    });
    this.list.addEventListener('mouseleave', () => {
      isDown = false;
    });
    this.list.addEventListener('mouseup', () => {
      isDown = false;
    });
    this.list.addEventListener('mousemove', event => {
      if (!isDown) return;
      event.preventDefault();
      const x = event.pageX - this.list.offsetLeft;
      const walk = (x - startX) * 3; //scroll-fast
      this.list.scrollLeft = scrollLeft - walk;
    });
  }

  /**
   * Scrolls to the item at the specified index
   * @param {Number} index - The index of the item to scroll to
   *
   * @note 'block: 'nearest' stops the vertical scrolling when the item is in view
   *
   * @returns {void}
   */
  scrollToIndex(index) {
    const target = this.querySelector(`[data-scroll-item="${index}"]`);
    target.scrollIntoView({
      block: 'nearest'
      // Note, adding 'behavior: "smooth"'here at times seemed to cause the scroll to not work. We set it globally in the CSS anyway.
    });
  }

  /**
   * Maintains a list of indexes of the items that are fully in view
   *
   * @returns {void}
   */
  maintainSetOfInViewIndexes() {
    this.observer = new IntersectionObserver(
      // eslint-disable-next-line no-unused-vars
      (entries, observer) => {
        for (const entry of entries) {
          const index = entry.target.dataset.scrollItem;
          entry.isIntersecting
            ? this.inViewIndexes.add(index)
            : this.inViewIndexes.delete(index);
        }

        this.enableAndDisableButtons();
      },
      { threshold: 0.5 }
    );

    for (const element of this.items) {
      this.observer.observe(element);
    }
  }

  /**
   * Scrolls to the next or previous item
   * @param {('previous' | 'next')} direction
   *
   * @returns {void}
   */
  goTo(direction) {
    if (direction === 'previous') {
      const targetIndex = Math.min(...this.inViewIndexes) - 1;
      if (targetIndex > 0) this.scrollToIndex(targetIndex);
    } else if (direction === 'next') {
      const targetIndex = Math.max(...this.inViewIndexes) + 1;
      if (targetIndex <= this.items.length) this.scrollToIndex(targetIndex);
    }
  }

  /**
   * Disables the arrow buttons when the first or last item is in view
   *
   * @returns {void}
   */
  enableAndDisableButtons() {
    if (!this.leftButton || !this.rightButton) return;

    const lowestIndex = Math.min(...this.inViewIndexes);
    const highestIndex = Math.max(...this.inViewIndexes);

    if (lowestIndex === 1 && highestIndex === this.items.length) {
      this.leftButton.disabled = true;
      this.rightButton.disabled = true;
    } else if (lowestIndex === 1) {
      this.leftButton.disabled = true;
      this.rightButton.disabled = false;
    } else if (highestIndex === this.items.length) {
      this.leftButton.disabled = false;
      this.rightButton.disabled = true;
    } else if (this.items.length === 0) {
      this.leftButton.disabled = true;
      this.rightButton.disabled = true;
    } else {
      this.leftButton.disabled = false;
      this.rightButton.disabled = false;
    }
  }
}

customElements.get('horizontal-scroll-carousel') ||
  customElements.define('horizontal-scroll-carousel', HorizontalScrollCarousel);
